/**
 * IncidentCard Component - Card component for displaying incidents
 * Epic 6: Quality/Incidents/Exceptions
 */

import React from "react";
import { Card, Typography, Box, Chip } from "@mui/material";
import { Warning, Schedule, Person } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";

const StyledCard = styled(Card)(({ theme, severity }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  transition: "all 0.2s ease",
  cursor: "pointer",
  border: "1px solid transparent",
  borderLeft: `4px solid ${
    severity === "CRITICAL"
      ? theme.palette.error.main
      : severity === "HIGH"
      ? theme.palette.warning.main
      : severity === "MEDIUM"
      ? theme.palette.info.main
      : theme.palette.success.main
  }`,
  "&:hover": {
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
    transform: "translateY(-1px)",
    borderColor: "rgba(0, 0, 0, 0.12)",
  },
}));

const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  marginBottom: 12,
});

const IncidentIdText = styled(Typography)({
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.6)",
  fontFamily: "monospace",
});

const TypeText = styled(Typography)({
  fontWeight: 600,
  fontSize: "1rem",
  marginBottom: 8,
});

const DescriptionText = styled(Typography)({
  fontSize: "0.875rem",
  color: "rgba(0, 0, 0, 0.7)",
  marginBottom: 12,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

const MetaRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 16,
  marginBottom: 12,
  flexWrap: "wrap",
});

const MetaItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.75rem",
  color: "rgba(0, 0, 0, 0.6)",
});

const BadgesRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "center",
});

const getSeverityColor = (severity) => {
  switch (severity) {
    case "CRITICAL":
      return "error";
    case "HIGH":
      return "warning";
    case "MEDIUM":
      return "info";
    case "LOW":
      return "success";
    default:
      return "default";
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "OPEN":
      return "error";
    case "UNDER_REVIEW":
      return "warning";
    case "AWAITING_EVIDENCE":
      return "info";
    case "RESOLVED":
      return "success";
    case "CLOSED":
      return "default";
    default:
      return "default";
  }
};

/**
 * IncidentCard Component
 * @param {Object} props
 * @param {Object} props.incident - Incident data
 * @param {Function} props.onClick - Click handler
 */
const IncidentCard = ({ incident, onClick }) => {
  const handleClick = () => {
    if (onClick) {
      onClick(incident);
    }
  };

  return (
    <StyledCard severity={incident.severity} onClick={handleClick}>
      <CardContent>
        <HeaderRow>
          <Box flex={1}>
            <IncidentIdText>{incident.id}</IncidentIdText>
            <TypeText>{incident.type?.replace(/_/g, " ")}</TypeText>
          </Box>
          <BadgesRow>
            <Chip
              label={incident.severity}
              color={getSeverityColor(incident.severity)}
              size="small"
            />
            <Chip
              label={incident.status}
              color={getStatusColor(incident.status)}
              size="small"
            />
          </BadgesRow>
        </HeaderRow>

        <DescriptionText>{incident.description}</DescriptionText>

        <MetaRow>
          <MetaItem>
            <Schedule fontSize="small" />
            <Typography variant="caption">
              {format(new Date(incident.occurredAt), "MMM d, yyyy HH:mm")}
            </Typography>
          </MetaItem>
          {incident.reportedBy && (
            <MetaItem>
              <Person fontSize="small" />
              <Typography variant="caption">
                {incident.reportedBy.actorType}
              </Typography>
            </MetaItem>
          )}
          {incident.currentResponsibility && (
            <MetaItem>
              <Warning fontSize="small" />
              <Typography variant="caption">
                {incident.currentResponsibility}
              </Typography>
            </MetaItem>
          )}
        </MetaRow>
      </CardContent>
    </StyledCard>
  );
};

export default IncidentCard;
